<template>
  <div class="auth-callback">
    <div v-if="pendingVerification" class="confirm">
      <h1>Inloggen bij het portaal</h1>
      <p>Klik op de knop hieronder om uw aanmelding te bevestigen.</p>
      <Button
        :loading="verifying"
        label="Inloggen"
        icon="pi pi-sign-in"
        class="w-full"
        @click="confirmSignIn"
      />
      <p class="muted">Deze extra stap voorkomt dat uw inloglink ongeldig wordt gemaakt door e-mailbeveiliging.</p>
    </div>
    <div v-else-if="loading" class="loading">
      <p>Bezig met inloggen...</p>
    </div>
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import Button from 'primevue/button'
  import type { EmailOtpType, Session } from '@supabase/supabase-js'
  import { supabase } from '../lib/supabase'
  import {
    buildAuthLinkRecoveryRoute,
    clearStoredPostLoginRedirect,
    getStoredPostLoginRedirect,
    resolvePortalRouteForUser,
    sanitizeEmailPrefill,
    sanitizeRedirectTarget,
  } from '../utils/authNavigation'

  const router = useRouter()
  const route = useRoute()
  const loading = ref(true)
  const error = ref('')
  const pendingVerification = ref(false)
  const verifying = ref(false)

  const validEmailOtpTypes = new Set<EmailOtpType>(['signup', 'invite', 'magiclink', 'recovery', 'email_change', 'email'])

  const getRedirectTarget = () => {
    const queryRedirect = typeof route.query.redirect === 'string' ? route.query.redirect : null
    return sanitizeRedirectTarget(queryRedirect || getStoredPostLoginRedirect() || '/')
  }

  const getEmailPrefill = () => sanitizeEmailPrefill(typeof route.query.email === 'string' ? route.query.email : '')

  const hasAuthMarkers = () => {
    const authQueryKeys = ['code', 'token_hash', 'type', 'error', 'error_code', 'error_description']
    return authQueryKeys.some((key) => typeof route.query[key] === 'string')
  }

  const getPendingTokenHash = () => {
    const tokenHash = typeof route.query.token_hash === 'string' ? route.query.token_hash : null
    const queryType = typeof route.query.type === 'string' ? route.query.type : null
    const type = queryType && validEmailOtpTypes.has(queryType as EmailOtpType)
      ? queryType as EmailOtpType
      : null

    if (!tokenHash || !type) {
      return null
    }

    return { tokenHash, type }
  }

  const isAbortError = (err: unknown) =>
    (err instanceof Error && err.name === 'AbortError') ||
    (err instanceof Error && /signal is aborted/i.test(err.message))

  // gotrue-js serialises auth calls behind a cross-tab navigator lock; with
  // multiple portal tabs open, acquiring it can time out and abort
  // (Sentry PORTAL-VUE-9/-C). Retrying after a short pause recovers.
  const getSessionWithRetry = async () => {
    try {
      return await supabase.auth.getSession()
    } catch (err) {
      if (!isAbortError(err)) throw err
      await new Promise((resolve) => setTimeout(resolve, 1000))
      return await supabase.auth.getSession()
    }
  }

  const navigateWithSession = async (session: Session) => {
    const redirectTarget = getRedirectTarget()

    try {
      const destination = await resolvePortalRouteForUser(session.user.id, redirectTarget)
      clearStoredPostLoginRedirect()
      router.push(destination)
    } catch (typeCheckError) {
      console.warn('Error checking user type during auth callback:', typeCheckError)
      clearStoredPostLoginRedirect()
      router.push(redirectTarget)
    }
  }

  const redirectToLinkRecovery = (reason: 'expired-link' | 'invalid-link') => {
    clearStoredPostLoginRedirect()
    router.replace(buildAuthLinkRecoveryRoute({
      redirect: getRedirectTarget(),
      email: getEmailPrefill(),
      reason,
    }))
  }

  // Consumes the one-time token. Only ever called from the button's click
  // handler: mail scanners and link-tracking bots that prefetch (or even
  // render) the emailed link must not be able to invalidate it.
  const confirmSignIn = async () => {
    const pendingToken = getPendingTokenHash()
    if (!pendingToken || verifying.value) return

    verifying.value = true

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: pendingToken.tokenHash,
        type: pendingToken.type,
      })

      if (verifyError || !data.session) {
        throw verifyError ?? new Error('No session established after OTP verification')
      }

      pendingVerification.value = false
      loading.value = true
      await navigateWithSession(data.session)
    } catch (verifyError) {
      console.warn('Token hash verification failed during auth callback:', verifyError)
      pendingVerification.value = false
      redirectToLinkRecovery('expired-link')
    } finally {
      verifying.value = false
      loading.value = false
    }
  }

  onMounted(async () => {
    try {
      const { data: { session }, error: sessionError } = await getSessionWithRetry()

      if (!session && getPendingTokenHash()) {
        // Valid one-time login link and no existing session: wait for an
        // explicit click instead of verifying on mount, so automated fetches
        // of this page never consume the token.
        pendingVerification.value = true
        return
      }

      if (sessionError) throw sessionError

      if (session) {
        await navigateWithSession(session)
      } else if (hasAuthMarkers()) {
        redirectToLinkRecovery(typeof route.query.error === 'string' ? 'invalid-link' : 'expired-link')
      } else {
        error.value = 'Geen geldige login sessie gevonden.'
      }
    } catch (err) {
      console.error('Auth callback error:', err)
      error.value = isAbortError(err)
        ? 'Inloggen duurde te lang. Sluit eventuele andere tabbladen van het portaal en probeer het opnieuw.'
        : err instanceof Error ? err.message : 'Er is een fout opgetreden tijdens het inloggen.'
    } finally {
      loading.value = false
    }
  })
</script>

<style scoped>
  .auth-callback {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 2rem;
  }

  .loading,
  .error,
  .confirm {
    text-align: center;
    padding: 2rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .confirm {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: min(100%, 24rem);
  }

  .confirm h1 {
    margin: 0;
    font-size: 1.5rem;
  }

  .confirm p {
    margin: 0;
  }

  .muted {
    color: #6b7280;
    font-size: 0.8rem;
  }

  .w-full {
    width: 100%;
  }

  .error {
    color: #ef4444;
  }
</style>
